// Gmail-style search query helpers for the SPA. The BFF parses the operators
// itself (MailQueryParser.cs) — this side only tokenizes for the filter chips,
// the highlight terms and the advanced-search form. Keep the key list in step
// with the server's.

const KNOWN_KEYS = new Set([
    "from", "to", "cc", "subject", "has", "is", "in", "label",
    "before", "older", "after", "newer", "larger", "size", "smaller"
]);

export interface SearchToken {
    /** Lower-case operator, or null for free text. */
    key: string | null;
    value: string;
    negated: boolean;
    quoted: boolean;
}

const isSpace = (c: string | undefined) => c !== undefined && /\s/.test(c);

function readQuoted(q: string, start: number): [string, number] {
    let i = start + 1;
    while (i < q.length && q[i] !== '"') i++;
    const value = q.slice(start + 1, i).trim();
    return [value, i < q.length ? i + 1 : i];
}

export function tokenizeQuery(q: string): SearchToken[] {
    const out: SearchToken[] = [];
    let i = 0;
    while (i < q.length) {
        while (i < q.length && isSpace(q[i])) i++;
        if (i >= q.length) break;

        let negated = false;
        if (q[i] === "-" && i + 1 < q.length && !isSpace(q[i + 1])) {
            negated = true;
            i++;
        }
        if (q[i] === '"') {
            const [value, next] = readQuoted(q, i);
            i = next;
            if (value) out.push({ key: null, value, negated, quoted: true });
            continue;
        }

        const start = i;
        let key: string | null = null;
        let value = "";
        let quoted = false;
        while (i < q.length && !isSpace(q[i])) {
            if (q[i] === ":" && key === null) {
                const candidate = q.slice(start, i).toLowerCase();
                if (KNOWN_KEYS.has(candidate)) {
                    key = candidate;
                    i++;
                    if (q[i] === '"') {
                        const [v, next] = readQuoted(q, i);
                        value = v;
                        i = next;
                        quoted = true;
                        break;
                    }
                    value = "";
                    continue;
                }
            }
            value += q[i];
            i++;
        }
        if (key === null) value = q.slice(start, i);
        value = value.trim();
        if (key && ["has", "is", "in", "label"].includes(key)) value = value.toLowerCase();
        if (value || key) out.push({ key, value, negated, quoted });
    }
    return out;
}

const needsQuotes = (v: string) => /\s/.test(v);

export function serializeToken(t: SearchToken): string {
    const neg = t.negated ? "-" : "";
    if (!t.key) return neg + (t.quoted || needsQuotes(t.value) ? `"${t.value}"` : t.value);
    return `${neg}${t.key}:${needsQuotes(t.value) ? `"${t.value}"` : t.value}`;
}

export function serializeTokens(tokens: SearchToken[]): string {
    return tokens.map(serializeToken).join(" ").trim();
}

/** The query without its index-th token (for removable chips). */
export function removeTokenAt(q: string, index: number): string {
    return serializeTokens(tokenizeQuery(q).filter((_, i) => i !== index));
}

/** Human label for a chip. */
export function describeToken(t: SearchToken): string {
    const v = t.value;
    switch (t.key) {
        case "from": return `From: ${v}`;
        case "to":
        case "cc": return `To: ${v}`;
        case "subject": return `Subject: ${v}`;
        case "has": return v.startsWith("attachment") ? (t.negated ? "No attachment" : "Has attachment") : `has:${v}`;
        case "is":
            if (v === "unread") return t.negated ? "Read" : "Unread";
            if (v === "read") return t.negated ? "Unread" : "Read";
            if (v === "starred" || v === "flagged") return t.negated ? "Not starred" : "Starred";
            return `is:${v}`;
        case "in":
        case "label": return v === "anywhere" || v === "all" ? "All folders incl. Trash & Spam" : `In: ${v}`;
        case "before":
        case "older": return `Before ${v}`;
        case "after":
        case "newer": return `After ${v}`;
        case "larger":
        case "size": return `Larger than ${v}`;
        case "smaller": return `Smaller than ${v}`;
        default:
            return t.negated ? `Without: ${t.quoted ? `"${v}"` : v}` : t.quoted ? `"${v}"` : v;
    }
}

/** Text to highlight in result rows: words, phrases and from/to/subject values. */
export function highlightTerms(q: string): string[] {
    const terms = new Set<string>();
    for (const t of tokenizeQuery(q)) {
        if (t.negated) continue;
        if (t.key === null || t.key === "from" || t.key === "to" || t.key === "cc" || t.key === "subject") {
            if (t.value.length >= 2) terms.add(t.value);
        }
    }
    return Array.from(terms);
}

// ── Advanced search form ⇄ query string ──────────────────────────────────

export type DateWithin = "" | "1d" | "3d" | "1w" | "2w" | "1m" | "2m" | "6m" | "1y";

export const DATE_WITHIN_OPTIONS: Array<{ value: DateWithin; label: string; days: number }> = [
    { value: "", label: "Any time", days: 0 },
    { value: "1d", label: "1 day", days: 1 },
    { value: "3d", label: "3 days", days: 3 },
    { value: "1w", label: "1 week", days: 7 },
    { value: "2w", label: "2 weeks", days: 14 },
    { value: "1m", label: "1 month", days: 30 },
    { value: "2m", label: "2 months", days: 60 },
    { value: "6m", label: "6 months", days: 182 },
    { value: "1y", label: "1 year", days: 365 }
];

export interface AdvancedSearch {
    from: string;
    to: string;
    subject: string;
    hasWords: string;
    doesntHave: string;
    dateWithin: DateWithin;
    /** YYYY-MM-DD the "within" window is centred on. */
    dateAnchor: string;
    sizeOp: "larger" | "smaller";
    size: string;
    sizeUnit: "M" | "K" | "B";
    /** Folder id, "" = all mail (not Trash/Spam), "anywhere" = everything. */
    folder: string;
    hasAttachment: boolean;
    /** Operators the form has no field for (is:unread, a lone before:) — kept as-is. */
    extra: string;
}

export function toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function parseIsoDate(v: string): Date | null {
    const m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(v.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
}

function addDays(d: Date, days: number): Date {
    const next = new Date(d);
    next.setDate(next.getDate() + days);
    return next;
}

export function emptyAdvancedSearch(): AdvancedSearch {
    return {
        from: "", to: "", subject: "", hasWords: "", doesntHave: "",
        dateWithin: "", dateAnchor: toIsoDate(new Date()),
        sizeOp: "larger", size: "", sizeUnit: "M",
        folder: "", hasAttachment: false, extra: ""
    };
}

export function buildQueryFromForm(f: AdvancedSearch): string {
    const tokens: SearchToken[] = [];
    const op = (key: string, value: string) => {
        const v = value.trim();
        if (v) tokens.push({ key, value: v, negated: false, quoted: false });
    };
    op("from", f.from);
    op("to", f.to);
    op("subject", f.subject);
    for (const t of tokenizeQuery(f.hasWords)) tokens.push(t);
    for (const t of tokenizeQuery(f.doesntHave)) {
        if (t.key === null) tokens.push({ ...t, negated: true });
    }
    const days = DATE_WITHIN_OPTIONS.find((o) => o.value === f.dateWithin)?.days ?? 0;
    const anchor = parseIsoDate(f.dateAnchor);
    if (days > 0 && anchor) {
        op("after", toIsoDate(addDays(anchor, -days)));
        op("before", toIsoDate(addDays(anchor, days + 1)));
    }
    const size = f.size.trim();
    if (size && /^\d+(\.\d+)?$/.test(size)) op(f.sizeOp, f.sizeUnit === "B" ? size : `${size}${f.sizeUnit}`);
    op("in", f.folder);
    if (f.hasAttachment) op("has", "attachment");
    for (const t of tokenizeQuery(f.extra)) tokens.push(t);
    return serializeTokens(tokens);
}

/** Best-effort inverse of buildQueryFromForm — pre-fills the form from the current search. */
export function formFromQuery(q: string): AdvancedSearch {
    const f = emptyAdvancedSearch();
    const words: SearchToken[] = [];
    const excluded: SearchToken[] = [];
    const extra: SearchToken[] = [];
    let after: Date | null = null;
    let before: Date | null = null;
    for (const t of tokenizeQuery(q)) {
        if (t.key === null) {
            (t.negated ? excluded : words).push(t);
            continue;
        }
        if (t.negated) { extra.push(t); continue; }
        switch (t.key) {
            case "from": f.from = t.value; break;
            case "to":
            case "cc": f.to = t.value; break;
            case "subject": f.subject = t.value; break;
            case "in":
            case "label": f.folder = t.value; break;
            case "has":
                if (t.value.startsWith("attachment")) f.hasAttachment = true;
                else extra.push(t);
                break;
            case "after":
            case "newer": after = parseIsoDate(t.value); if (!after) extra.push(t); break;
            case "before":
            case "older": before = parseIsoDate(t.value); if (!before) extra.push(t); break;
            case "larger":
            case "size":
            case "smaller": {
                const m = /^(\d+(?:\.\d+)?)\s*([kmg]?)b?$/i.exec(t.value);
                if (m && (m[2] ?? "").toUpperCase() !== "G") {
                    f.sizeOp = t.key === "smaller" ? "smaller" : "larger";
                    f.size = m[1] ?? "";
                    const unit = (m[2] ?? "").toUpperCase();
                    f.sizeUnit = unit === "K" ? "K" : unit === "M" ? "M" : "B";
                } else extra.push(t);
                break;
            }
            default: extra.push(t);
        }
    }
    f.hasWords = serializeTokens(words);
    f.doesntHave = serializeTokens(excluded.map((t) => ({ ...t, negated: false })));

    // A symmetric after/before pair maps back to "Date within N of <date>".
    if (after && before) {
        const span = Math.round((before.getTime() - after.getTime()) / 86_400_000);
        const days = (span - 1) / 2;
        const option = DATE_WITHIN_OPTIONS.find((o) => o.days === days && o.days > 0);
        if (option) {
            f.dateWithin = option.value;
            f.dateAnchor = toIsoDate(addDays(after, days));
        } else {
            extra.push({ key: "after", value: toIsoDate(after), negated: false, quoted: false });
            extra.push({ key: "before", value: toIsoDate(before), negated: false, quoted: false });
        }
    } else if (after) {
        extra.push({ key: "after", value: toIsoDate(after), negated: false, quoted: false });
    } else if (before) {
        extra.push({ key: "before", value: toIsoDate(before), negated: false, quoted: false });
    }
    f.extra = serializeTokens(extra);
    return f;
}

/** The token being typed at the caret, when it is a from:/to: operator (for people suggestions). */
export function activePeopleToken(q: string, caret: number): { key: "from" | "to"; value: string; start: number; end: number } | null {
    let start = caret;
    while (start > 0 && !isSpace(q[start - 1])) start--;
    let end = caret;
    while (end < q.length && !isSpace(q[end])) end++;
    const word = q.slice(start, end);
    const m = /^-?(from|to|cc):(.*)$/i.exec(word);
    if (!m) return null;
    const key = (m[1] ?? "").toLowerCase() === "from" ? "from" : "to";
    return { key, value: (m[2] ?? "").replace(/^"/, ""), start, end };
}
